import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, User, Calendar, Tag } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface CompletedTask {
  id: string;
  title: string;
  description?: string;
  assignee_name?: string;
  completed_date?: string;
  priority: number;
  project?: string;
  tags?: string[];
}

export const RecentCompletedTasks = ({ limit = 10 }: { limit?: number }) => {
  const { data: completedTasks = [], isLoading } = useQuery({
    queryKey: ['recent-completed-tasks', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('completed', true)
        .order('completed_date', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as CompletedTask[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Recently Completed Tasks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (completedTasks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Recently Completed Tasks
          </CardTitle>
          <CardDescription>Tasks that have been marked as complete</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No completed tasks yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          Recently Completed Tasks
        </CardTitle>
        <CardDescription>
          {completedTasks.length} task{completedTasks.length !== 1 ? 's' : ''} completed recently
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {completedTasks.map((task) => (
            <div
              key={task.id}
              className="border rounded-lg p-4 space-y-2 bg-green-50/50 dark:bg-green-900/10 hover:bg-green-100/50 dark:hover:bg-green-900/20 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <h3 className="font-medium text-sm">{task.title}</h3>
                  </div>
                  {task.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {task.description}
                    </p>
                  )}
                </div>
                {task.priority && task.priority <= 50 && (
                  <Badge
                    variant="outline"
                    className={
                      task.priority <= 10
                        ? 'border-red-500 text-red-700 bg-red-50'
                        : 'border-orange-500 text-orange-700 bg-orange-50'
                    }
                  >
                    P{task.priority}
                  </Badge>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {task.assignee_name && (
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>{task.assignee_name}</span>
                  </div>
                )}
                {task.completed_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>
                      {formatDistanceToNow(new Date(task.completed_date), { addSuffix: true })}
                    </span>
                  </div>
                )}
                {task.project && (
                  <Badge variant="secondary" className="text-xs">
                    {task.project}
                  </Badge>
                )}
                {task.tags && task.tags.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    <span className="flex gap-1">
                      {task.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};